/**
 * Utilitários para verificação e diagnóstico de autenticação
 */

/**
 * Verifica se o usuário está devidamente autenticado
 * @param {Object} user - Objeto do usuário
 * @returns {Object} Resultado da verificação
 */
export const verificarAutenticacao = (user) => {
  const resultado = {
    valido: false,
    erros: [],
    warnings: [],
    user: user
  };

  // Verificar se o objeto user existe
  if (!user) {
    resultado.erros.push('Objeto user é null ou undefined');
    return resultado;
  }

  // Verificar se o user.id existe e é válido
  if (!user.id || user.id === undefined || user.id === null) {
    resultado.erros.push('user.id é null, undefined ou vazio');
  }

  // Verificar se o user.id é um número válido
  if (user.id && (isNaN(user.id) || user.id <= 0)) {
    resultado.erros.push(`user.id não é um número válido: ${user.id}`);
  }

  // Verificar se o usuário tem nome
  if (!user.nome || user.nome.trim() === '') {
    resultado.warnings.push('user.nome está vazio ou não existe');
  }

  // Verificar se o usuário tem cargo
  if (!user.cargo) {
    resultado.warnings.push('user.cargo não existe');
  }

  // Verificar se o usuário está ativo
  if (user.ativo === false) {
    resultado.warnings.push('Usuário está marcado como inativo');
  }

  // Verificar se há token de autenticação
  const token = localStorage.getItem('@SenhasApp:token') || 
               localStorage.getItem('token') || 
               sessionStorage.getItem('@SenhasApp:token');

  if (!token) {
    resultado.warnings.push('Token de autenticação não encontrado no localStorage/sessionStorage');
  }

  // Se não há erros críticos, marcar como válido
  resultado.valido = resultado.erros.length === 0;

  return resultado;
};

/**
 * Imprime diagnóstico detalhado do usuário no console
 * @param {Object} user - Objeto do usuário
 * @param {string} contexto - Contexto onde a verificação está sendo feita
 */
export const diagnosticarUsuario = (user, contexto = 'Desconhecido') => {
  console.group(`🔍 Diagnóstico de Usuário - Contexto: ${contexto}`);
  
  const verificacao = verificarAutenticacao(user);
  
  console.log('📋 Dados do usuário:', user);
  console.log('✅ Verificação válida:', verificacao.valido);
  
  if (verificacao.erros.length > 0) {
    console.error('❌ Erros encontrados:', verificacao.erros);
  }
  
  if (verificacao.warnings.length > 0) {
    console.warn('⚠️ Avisos:', verificacao.warnings);
  }

  // Verificar tokens disponíveis
  const tokens = {
    '@SenhasApp:token (localStorage)': localStorage.getItem('@SenhasApp:token'),
    'token (localStorage)': localStorage.getItem('token'),
    'authToken (localStorage)': localStorage.getItem('authToken'),
    '@SenhasApp:token (sessionStorage)': sessionStorage.getItem('@SenhasApp:token'),
    'token (sessionStorage)': sessionStorage.getItem('token'),
    'authToken (sessionStorage)': sessionStorage.getItem('authToken')
  };

  console.log('🔑 Tokens disponíveis:');
  Object.entries(tokens).forEach(([chave, valor]) => {
    if (valor) {
      console.log(`  ✅ ${chave}: ${valor.substring(0, 20)}...`);
    } else {
      console.log(`  ❌ ${chave}: não encontrado`);
    }
  });
  
  console.groupEnd();
  
  return verificacao;
};

/**
 * Verifica se o usuário pode realizar transferências
 * @param {Object} user - Objeto do usuário
 * @returns {boolean} True se pode transferir
 */
export const podeTransferir = (user) => {
  const verificacao = verificarAutenticacao(user);
  
  if (!verificacao.valido) {
    console.error('❌ Usuário não pode transferir senhas:', verificacao.erros);
    return false;
  }

  // Verificar se o cargo permite transferência
  const cargosPermitidos = ['ATENDENTE', 'ADMIN', 'RECEPCAO'];
  if (!cargosPermitidos.includes(user.cargo)) {
    console.warn(`⚠️ Cargo ${user.cargo} pode não ter permissão para transferir`);
  }

  return true;
};

/**
 * Obtém o ID do usuário de forma segura
 * @param {Object} user - Objeto do usuário
 * @returns {number|null} ID do usuário ou null se inválido
 */
export const obterIdUsuario = (user) => {
  const verificacao = verificarAutenticacao(user);
  
  if (!verificacao.valido || !user.id) {
    return null;
  }

  return parseInt(user.id);
};

export default {
  verificarAutenticacao,
  diagnosticarUsuario,
  podeTransferir,
  obterIdUsuario
}; 